import { useVisualViewport } from './hooks/useVisualViewport';
import React, { Suspense, lazy, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { LanguageProvider } from '@/context/LanguageContext';
import { CurrencyProvider } from '@/context/CurrencyContext';
import { initAnalytics, trackPageView } from '@/lib/analytics';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';

// Code-split everything except the landing/login entry.
const Pricing = lazy(() => import('@/pages/Pricing'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Templates = lazy(() => import('@/pages/Templates'));
const TemplateDetail = lazy(() => import('@/pages/TemplateDetail'));
const Features = lazy(() => import('@/pages/Features'));
const HowItWorks = lazy(() => import('@/pages/HowItWorks'));
const Faq = lazy(() => import('@/pages/Faq'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const Builder = lazy(() => import('@/pages/Builder'));
const Settings = lazy(() => import('@/pages/Settings'));
const PublicResume = lazy(() => import('@/pages/PublicResume'));
const ResumeBuilderIndia = lazy(() => import('@/pages/ResumeBuilderIndia'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const RefundPolicy = lazy(() => import('@/pages/RefundPolicy'));
const CookiePolicy = lazy(() => import('@/pages/CookiePolicy'));
const NotFound = lazy(() => import('@/pages/NotFound'));

const FullScreenLoader = () => (
  <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  return user ? children : <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  if (!user) return children;
  const next = new URLSearchParams(location.search).get('next');
  return <Navigate to={next && next.startsWith('/') ? next : '/dashboard'} replace />;
};

// Scroll to top (or to #hash) and record a page view on every route change.
const RouteEffects = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    }
    window.scrollTo({ top: 0, left: 0 });
  }, [location.pathname, location.hash]);
  useEffect(() => { trackPageView(location.pathname + location.search); }, [location.pathname, location.search]);
  return null;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <Suspense fallback={<FullScreenLoader />}><AuthCallback /></Suspense>;
  }
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <RouteEffects />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/templates/:templateId" element={<TemplateDetail />} />
        <Route path="/features" element={<Features />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/resume-builder-india" element={<ResumeBuilderIndia />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/r/:shareId" element={<PublicResume />} />
        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/builder/:id" element={<ProtectedRoute><Builder /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useVisualViewport();
  useEffect(() => { initAnalytics(); }, []);
  return (
    <LanguageProvider>
      <AuthProvider>
        <CurrencyProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </CurrencyProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
